import React, { useState, useEffect } from "react";

// components:
import CardLinks from "./CardLinks";
import OrderSummary from "./OrderSummary";
import FormButtons from "./FormButtons";
import PhotosOrderPreview from "core/CheckoutForm/PhotosOrderPreview";
import VatInput from "core/CheckoutForm/VatInput";
import PaymentMethodRadioButtons from "./PaymentMethodRadioButtons";
import PaymentInfo from "./PaymentInfo";
import CheckoutPathFormButtons from "./CheckoutPathFormButtons";
import PromoCode from "subscriptions/PromoCode";

// helpers
import { usePromoCode } from "frontend/context/CheckoutContext";
import {
    PhotoCheckoutPropTypes,
    IconCheckoutPropTypes,
    StandaloneSubscriptionPropTypes
} from "./helpers";
import { PAYMENT_METHODS } from "./constants";

// styles:
import {
    StyledCheckoutForm,
    FormHeading,
    PromoCodePayPalWarning
} from "./styles";

const CheckoutForm = ({
    cancelLink, // url when click cancel
    className,
    form, // form used for CC info, name, zip, receiptInfo (VAT)
    // CardLinks props:
    // SavedCardForm/EmptyForm props:
    handleFormChange, // updates form object in parent,
    hasSavedCard, // true if CC can be populated
    hideCardLinks = true, // disable option to save CC
    isPhotoCheckout = false, // determines layout for PhotoCheckout
    isSubmitDisabled, // used on purchase button when payment is processing
    itemPrice,
    itemPriceOrderSummaryItem,
    itemTitle,
    licenseSchemas, // used in OrderPreview to show which photo res (Large or Full) download they are purchasing
    licenseSelection,
    loading, // used on purchase button when payment is processing,
    numSeats, // number of persons on team
    orderSummaryTextLine1, // Order Summary info only on icon checkout
    orderSummaryTextLine2, // Order Summary info only on icon checkout
    response = {}, // response from GET_SAVED_CARD which is processed in getFormattedData
    saveCardCheckboxOnClick,
    saveCardInitialValue,
    showOrderSummary = true,
    showPayPalPayment = false,
    showPromoCode = false, // only show on subscription for now
    slugWithId, // Icon or Photo slug with ID
    submitFormWithNewCardInfo, // submit function If user manually  typed in CC info
    submitFormWithSavedCardInfo, // submit function If user checked out with populated CC info from GET_SAVED_CARD
    submitText, // purchase button text
    thumbnail, // thumbnail used for Photo OrderPreview or Icon OrderSummary
    title // item title
}) => {
    const [showSavedCard, setShowSavedCard] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState(
        PAYMENT_METHODS.CREDIT_CARD
    );

    const { promoCodeValue, setPromoCodeValue } = usePromoCode();

    useEffect(() => {
        if (hasSavedCard) setShowSavedCard(hasSavedCard);
    }, [hasSavedCard]);

    // on load it will default to CC but promocode is an empty string so its false.
    // If the user enters a promo code, then and then switches to the paypal tab,
    // this will NOT run so we can display an info banner message that paypal doesnt accept
    // promo codes. Once the user goes back to CC, this runs and we wipe out the value
    // to be in line with stripe wiping out the CC values
    useEffect(() => {
        if (paymentMethod === PAYMENT_METHODS.CREDIT_CARD && promoCodeValue)
            setPromoCodeValue("");
    }, [paymentMethod]);

    const handleUseCardClick = e => {
        e.preventDefault();
        setShowSavedCard(!showSavedCard);
    };

    const isCreditCard = paymentMethod === PAYMENT_METHODS.CREDIT_CARD;

    let formButtonsComponent;

    if (showPayPalPayment) {
        formButtonsComponent = (
            <CheckoutPathFormButtons
                purchaseButtonOnClick={
                    showSavedCard
                        ? submitFormWithSavedCardInfo
                        : submitFormWithNewCardInfo
                }
                showPayPalButton={paymentMethod === PAYMENT_METHODS.PAYPAL}
                isProcessing={loading}
                isDisabled={isSubmitDisabled}
                cancelLink={cancelLink}
                licenseSelection={licenseSelection}
            />
        );
    } else {
        formButtonsComponent = (
            <FormButtons
                showPayPalButton={paymentMethod === PAYMENT_METHODS.PAYPAL}
                isSubmitDisabled={isSubmitDisabled}
                showSavedCard={showSavedCard}
                submitFormWithNewCardInfo={submitFormWithNewCardInfo}
                submitFormWithSavedCardInfo={submitFormWithSavedCardInfo}
                cancelLink={cancelLink}
                submitText={submitText}
                loading={loading}
            />
        );
    }

    return (
        <div className={className}>
            {isPhotoCheckout && (
                <PhotosOrderPreview
                    licenseSchemas={licenseSchemas}
                    title={title}
                    thumbnail={thumbnail}
                    slugWithId={slugWithId}
                />
            )}

            <FormHeading>Payment Information</FormHeading>

            {!hideCardLinks && (
                <CardLinks
                    showSavedCard={showSavedCard}
                    hasSavedCard={hasSavedCard}
                    handleUseCardClick={handleUseCardClick}
                    saveCardCheckboxOnClick={saveCardCheckboxOnClick}
                    saveCardInitialValue={saveCardInitialValue}
                />
            )}

            <StyledCheckoutForm>
                {showPayPalPayment && (
                    <PaymentMethodRadioButtons
                        setPaymentMethod={setPaymentMethod}
                    />
                )}
                <form className="stripe-form">
                    <PaymentInfo
                        paymentMethod={paymentMethod}
                        form={form}
                        response={response}
                        handleFormChange={handleFormChange}
                        showSavedCard={showSavedCard}
                    />

                    {/*For now, we do not want promo code to be shown on PayPal*/}
                    {isCreditCard && showPromoCode && (
                        <PromoCode numSeats={numSeats} />
                    )}

                    {/* If the user entered a code & switches to paypal we show this message. */}
                    {!isCreditCard && promoCodeValue && (
                        <PromoCodePayPalWarning>
                            Promo code removed. Buy with Card to use.
                        </PromoCodePayPalWarning>
                    )}

                    {/*
                    This is optional VAT (Add Billing Info) info that shows up in the gray box on invoices
                    Note: For Icon & Subscriptions a dedicated API to update VAT must be hit before purchase. If that API isnt hit first,
                    then the changed VAT input will not show up on the users invoice.
                    In the future, BE should add VAT info to the purchase API like how it exists for the Photos GQL purchase.
                    */}
                    <VatInput />

                    <OrderSummary
                        thumbnail={thumbnail}
                        itemTitle={itemTitle}
                        itemPrice={itemPrice}
                        itemPriceOrderSummaryItem={itemPriceOrderSummaryItem}
                        orderSummaryTextLine1={orderSummaryTextLine1}
                        orderSummaryTextLine2={orderSummaryTextLine2}
                        showOrderSummary={showOrderSummary}
                        licenseSelection={licenseSelection}
                        isPhotoCheckout={isPhotoCheckout}
                    />

                    {formButtonsComponent}
                </form>
            </StyledCheckoutForm>
        </div>
    );
};

CheckoutForm.propTypes = {
    PhotoCheckoutPropTypes,
    IconCheckoutPropTypes,
    StandaloneSubscriptionPropTypes
};

export default CheckoutForm;
